'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

import type { InsightsSeriesItem } from '../data/insights';

const LazyInsightsChart = dynamic(() => import('./insights-chart-content'), {
  ssr: false,
  loading: () => <ChartPlaceholder />,
});

function ChartPlaceholder() {
  return <div className="aspect-2/1 w-full sm:aspect-3/1" aria-hidden />;
}

/**
 * Viewport-gated wrapper: the chart sits below the fold, so the @visx/d3
 * runtime is only fetched once the section is close to being visible.
 */
export function InsightsChart({ series }: { series: InsightsSeriesItem[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {inView ? <LazyInsightsChart series={series} /> : <ChartPlaceholder />}
    </div>
  );
}
